import React, { useState, useEffect } from 'react';
import { PlusCircle, MessageSquare, Edit, ArrowLeft, X } from 'lucide-react'; 
import UnsavedChangesModal from './UnsavedChangesModal'; // Import the external modal component

const Section = ({ title, children }) => (
    <div className="border rounded-lg bg-white shadow-sm">
        <h3 className="text-lg font-semibold text-gray-800 px-4 py-3 border-b bg-gray-50 rounded-t-lg">{title}</h3>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {children}
        </div>
    </div>
);



const CommentSection = ({ comments, recordId, onCommentAdded, user, api }) => {
    const [showCommentForm, setShowCommentForm] = useState(false);
    const [comment, setComment] = useState('');
    const [status, setStatus] = useState('');
    
    // --- NEW: State for Offer Amount ---
    const [offerAmount, setOfferAmount] = useState('');
    // -----------------------------------

    const [scheduledDate, setScheduledDate] = useState('');
    const [scheduledTime, setScheduledTime] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [timeSlots, setTimeSlots] = useState([]);

    // 🔐 Provider is read-only
    const isProvider = user?.role?.toLowerCase() === 'provider';

    const statusesWithoutEvent = ['payment_received'];
    const showScheduleFields = status && !statusesWithoutEvent.includes(status);
    
    // --- NEW: Condition to show Offer Amount ---
    const showOfferField = status === 'offer';
    // ------------------------------------------

    const handleStatusChange = (e) => {
        const newStatus = e.target.value;
        setStatus(newStatus);

        if (!newStatus || statusesWithoutEvent.includes(newStatus)) {
            setScheduledDate('');
            setScheduledTime('');
        }
        
        // Reset offer amount if status is not offer
        if (newStatus !== 'offer') {
            setOfferAmount('');
        }
    };

    const generateTimeSlots = (dateStr) => {
        if (!dateStr) return [];

        const slots = [];
        const selectedDate = new Date(`${dateStr}T00:00:00`);
        const now = new Date();

        let startHour = 0;
        let startMinute = 0;

        const isToday =
            selectedDate.getFullYear() === now.getFullYear() &&
            selectedDate.getMonth() === now.getMonth() &&
            selectedDate.getDate() === now.getDate();

        if (isToday) {
            startHour = now.getHours();
            let roundedMinute = Math.ceil((now.getMinutes() + 1) / 15) * 15;
            if (roundedMinute >= 60) {
                startHour += 1;
                startMinute = 0;
            } else {
                startMinute = roundedMinute;
            }
            if (startHour >= 24) return [];
        }

        for (let hour = startHour; hour < 24; hour++) {
            const minuteStart = hour === startHour ? startMinute : 0;
            for (let minute = minuteStart; minute < 60; minute += 15) {
                const h = hour.toString().padStart(2, '0');
                const m = minute.toString().padStart(2, '0');
                slots.push(`${h}:${m}`);
            }
        }

        return slots;
    };

    useEffect(() => {
        const newTimeSlots = generateTimeSlots(scheduledDate);
        setTimeSlots(newTimeSlots);

        if (scheduledDate && scheduledTime && !newTimeSlots.includes(scheduledTime)) {
            setScheduledTime('');
        }
    }, [scheduledDate, scheduledTime]);

    const statusOptions = [
        { value: 'callback', label: 'Call Back' },
        { value: 'lvm', label: 'Left Voice Message' },
        { value: 'spoke_to', label: 'Spoke To' },
        { value: 'sent_email_fax', label: 'Sent Email/Fax' },
        { value: 'received_email_fax', label: 'Recieved Email/Fax' },
        { value: 'offer', label: 'Offer' },
        { value: 'settle', label: 'Settle' },
        { value: 'request_to_close', label: 'Request to Close' },
        { value: 'out of sol', label: 'Out Of Sol' },
        { value: 'dor filed', label: 'Dor Filed' },
        { value: 'wfp', label: 'Waiting for Payment' },
        { value: 'file_pmr', label: 'File PMR' },
        { value: 'file_lien', label: 'File Lien' },
        { value: 'payment_received', label: 'Payment Received' },
        { value: 'hearing_remarks', label: 'Hearing Remarks' },
        ...(user?.role?.toLowerCase() === 'admin'
            ? [{ value: 'closed', label: 'Closed' }]
            : []),
    ];

    const getStatusBadge = (statusValue) =>
        ({
            callback: { label: 'Call Back', color: 'bg-blue-100 text-blue-800' },
            lvm: { label: 'Left Voice Message', color: 'bg-yellow-100 text-yellow-800' },
            spoke_to: { label: 'Spoke To', color: 'bg-green-100 text-green-800' },
            sent_email_fax: {
                label: 'Sent Email/Fax',
                color: 'bg-purple-100 text-purple-800',
            },
            received_email_fax: {
                label: 'Received Email/Fax',
                color: 'bg-pink-100 text-pink-800',
            },
            offer: { label: 'Offer', color: 'bg-indigo-100 text-indigo-800' },
            settle: { label: 'Settle', color: 'bg-pink-100 text-pink-800' },
            request_to_close: {
                label: 'Request to Close',
                color: 'bg-teal-100 text-teal-800',
            },
            'out of sol': { label: 'Out Of Sol', color: 'bg-gray-100 text-gray-800' },
            'dor filed': { label: 'Dor Filed', color: 'bg-cyan-100 text-cyan-800' },
            wfp: { label: 'Waiting for Payment', color: 'bg-orange-100 text-orange-800' },
            file_pmr: { label: 'File PMR', color: 'bg-violet-100 text-violet-800' },
            file_lien: { label: 'File Lien', color: 'bg-rose-100 text-rose-800' },
            payment_received: {
                label: 'Payment Received',
                color: 'bg-emerald-100 text-emerald-800',
            },
            hearing_remarks: {
                label: 'Hearing Remarks',
                color: 'bg-lime-100 text-lime-800',
            },
            closed: { label: 'Closed', color: 'bg-red-100 text-red-800' },
        }[statusValue] || { label: statusValue, color: 'bg-gray-100 text-gray-800' });

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Extra protection: Provider cannot submit even if button somehow shows
        if (isProvider) return;

        setError('');

        if (!comment.trim() || !status) {
            setError('Comment and Status fields are required.');
            return;
        }

        if (showScheduleFields && (!scheduledDate || !scheduledTime)) {
            setError('Follow-up Date and Time are required for this status.');
            return;
        }

        // --- NEW: Validation for Offer Amount ---
        if (showOfferField && !offerAmount) {
            setError('Offer Amount is required when status is Offer.');
            return;
        }
        // ----------------------------------------

        setIsSubmitting(true);

        try {
            const commentData = {
                text: comment.trim(),
                status,
            };

            if (showScheduleFields) {
                commentData.scheduledDate = scheduledDate;
                commentData.scheduledTime = scheduledTime;
            }

            // --- NEW: Add Offer Amount to Payload ---
            if (showOfferField) {
                commentData.offerAmount = parseFloat(offerAmount);
            }
            // ----------------------------------------

            // Parent handler will call api.post and update record
            const updatedRecord = await onCommentAdded(recordId, commentData);

            if (!updatedRecord && api) {
                // Fallback: refresh record from server if needed
                await onCommentAdded(recordId);
            }

            setComment('');
            setStatus('');
            setScheduledDate('');
            setScheduledTime('');
            setOfferAmount(''); // Reset offer amount
            setShowCommentForm(false);
        } catch (err) {
            console.error(err);
            setError('Failed to add comment. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const today = `${year}-${month}-${day}`;

    return (
        <div className="border rounded-lg bg-white shadow-sm">
            {/* HEADER */}
            <div className="flex justify-between items-center px-4 py-3 border-b bg-gray-50 rounded-t-lg">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Comments &amp; Activity Log
                </h3>

                {/* 🚫 Provider: no Add Comment button */}
                {!isProvider && (
                    <button
                        type="button"
                        onClick={() => setShowCommentForm((s) => !s)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg text-sm flex items-center gap-2"
                    >
                        {showCommentForm ? (
                            <X className="w-4 h-4" />
                        ) : (
                            <PlusCircle className="w-4 h-4" />
                        )}
                        {showCommentForm ? 'Cancel' : 'Add Comment'}
                    </button>
                )}
            </div>

            {/* FORM */}
            {showCommentForm && !isProvider && (
                <div className="p-4 bg-gray-50 border-b">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Comment *
                            </label>
                            <textarea
                                rows={3}
                                className="w-full p-2 border border-gray-300 rounded-md"
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Enter your comment..."
                                required
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Status *
                                </label>
                                <select
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                    value={status}
                                    onChange={handleStatusChange}
                                    required
                                >
                                    <option value="">Select status</option>
                                    {statusOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* --- NEW: Offer Amount Field --- */}
                            {showOfferField && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Offer Amount ($) *
                                    </label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                            <span className="text-gray-500 sm:text-sm">$</span>
                                        </div>
                                        <input
                                            type="number"
                                            className="block w-full rounded-md border-gray-300 p-2 pl-7 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                            placeholder="0.00"
                                            value={offerAmount}
                                            onChange={(e) => setOfferAmount(e.target.value)}
                                            step="0.01"
                                            required={showOfferField}
                                        />
                                    </div>
                                </div>
                            )}
                            {/* ------------------------------- */}

                            {showScheduleFields && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Follow-up Date *
                                        </label>
                                        <input
                                            type="date"
                                            className="w-full p-2 border border-gray-300 rounded-md"
                                            value={scheduledDate}
                                            onChange={(e) => setScheduledDate(e.target.value)}
                                            min={today}
                                            required={showScheduleFields}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Follow-up Time *
                                        </label>
                                        <select
                                            className="w-full p-2 border border-gray-300 rounded-md"
                                            value={scheduledTime}
                                            onChange={(e) => setScheduledTime(e.target.value)}
                                            disabled={!scheduledDate}
                                            required={showScheduleFields}
                                        >
                                            <option value="">Select time</option>
                                            {timeSlots.map((slot) => (
                                                <option key={slot} value={slot}>
                                                    {slot}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}
                        </div>

                        {error && (
                            <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">
                                {error}
                            </div>
                        )}

                        <div className="flex justify-end space-x-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowCommentForm(false);
                                    setComment('');
                                    setStatus('');
                                    setScheduledDate('');
                                    setScheduledTime('');
                                    setOfferAmount('');
                                    setError('');
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:bg-gray-400"
                            >
                                {isSubmitting ? 'Adding...' : 'Add Comment'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* LIST */}
            <div className="p-4">
                {comments && comments.length > 0 ? (
                    <div className="space-y-4">
                        {comments.map((c) => {
                            const badge = getStatusBadge(c.status);
                            let followUpDisplay = null;

                            if (c.scheduledDate) {
                                const eventDateObj = new Date(c.scheduledDate);
                                const eventDate = new Date(
                                    eventDateObj.getFullYear(),
                                    eventDateObj.getMonth(),
                                    eventDateObj.getDate()
                                );

                                const todayDate = new Date(today);
                                const isCompleted = c.isCompleted;
                                const isPastDue = !isCompleted && eventDate < todayDate;

                                const label = isCompleted
                                    ? 'Completed'
                                    : isPastDue
                                    ? 'Past Due'
                                    : 'Upcoming';

                                const color = isCompleted
                                    ? 'bg-green-100 text-green-700 border-green-300'
                                    : isPastDue
                                    ? 'bg-red-100 text-red-700 border-red-300'
                                    : 'bg-blue-100 text-blue-700 border-blue-300';

                                followUpDisplay = (
                                    <span
                                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${color}`}
                                    >
                                        {label}:{' '}
                                        {eventDate.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' })}{' '}
                                        {c.scheduledTime || ''}
                                    </span>
                                );
                            }

                            return (
                                <div
                                    key={c._id || c.id}
                                    className="border rounded-md p-3 bg-gray-50 flex flex-col gap-1"
                                >
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}
                                            >
                                                {badge.label}
                                            </span>
                            

                                            {/* --- NEW: Display Offer Amount in Log --- */}

                                            {c.offerAmount && (

                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-green-50 text-green-700 border border-green-200">

                                                    ${c.offerAmount}

                                                </span>

                                            )}

                                            {/* --------------------------------------- */}
                                            {followUpDisplay}
                                        </div>
                                        <span className="text-xs text-gray-500">
                                             {c.createdAt ? new Date(c.createdAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}                                        <span className="ml-1 text-gray-400">|</span>
                                             <span className="ml-1 font-medium text-gray-600">
                                           Commented By: {c.author?.username || c.createdBy || 'Unknown'}
                                            </span>
                                        </span>
                                    </div>

                                    <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                        {c.text}
                                    </p>

                                    {c.createdBy && (
                                        <p className="text-xs text-gray-500">
                                           By: {c.createdBy}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">No comments yet.</p>
                )}
            </div>
        </div>
    );
};



const RecordDetails = ({ 
    record, 
    onBack, 
    onSave, 
    onAssign, 
    collectors = [], 
    user, 
    api,
    // --- Removed Header Props ---
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [initialEditData, setInitialEditData] = useState({}); 
    const [isSaving, setIsSaving] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });
    const [isAssigning, setIsAssigning] = useState(false);
    const [selectedCollectorForAssign, setSelectedCollectorForAssign] = useState('');
    const [currentRecord, setCurrentRecord] = useState(record);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    
    // --- NEW: State for Ref ID Refreshing ---
    const [isRefreshingId, setIsRefreshingId] = useState(false);
    // ----------------------------------------

    // 🔐 Provider = view-only
    const isProvider = user?.role?.toLowerCase() === 'provider';

    // NEW: State to track what the "No" button should do (either 'back' or 'cancel')
    const [modalDiscardAction, setModalDiscardAction] = useState(null);

    // NEW: Options for Case Status dropdown
    const caseStatusOptions = [
        { value: '', label: 'Select status (default)' },
        { value: 'SETTLED', label: 'SETTLED' },
        { value: 'C & R (GRANTED)', label: 'C & R (GRANTED)' },
        { value: 'CIC PENDING', label: 'CIC PENDING' },
        { value: 'A & S GRANTED', label: 'A & S GRANTED' },
        { value: 'ADR CASE - SETTED AND PAID ADR', label: 'ADR CASE - SETTED AND PAID ADR' },
        { value: 'ORDER OF DISMISAAL OF CASE', label: 'ORDER OF DISMISAAL OF CASE' },

    ];
 // NEW: Options for Lien Status dropdown
    const lienStatusOptions = [
        { value: '', label: 'Select status (default)' },
        { value: 'Not Filed', label: 'Not Filed' },
        { value: 'Out of SOL', label: 'Out of SOL' },
        { value: 'Filed', label: 'Filed' },
        { value: 'Settled', label: 'Settled' },
    ];
    // Helper to create a deep copy for comparison
    const deepCopy = (data) => JSON.parse(JSON.stringify(data));

    // Helper to check for unsaved changes
    const hasUnsavedChanges = () => {
        return JSON.stringify(editData) !== JSON.stringify(initialEditData);
    };
    
    // Helper to reset edit form to its initial state
    const resetEditData = () => {
        setEditData(deepCopy(initialEditData));
    };

    useEffect(() => {
        const initialData = {};
        Object.keys(record).forEach(key => {
            if (['hearingDate', 'caseDate', 'dob', 'fds', 'lds', 'solDate'].includes(key) && record[key]) {
                try {
                    // This logic is correct for populating the <input type="date">
                    // It converts the UTC timestamp (e.g., 2025-11-14T00:00:00Z)
                    // into an ISO string and takes the date part ("2025-11-14").
                    initialData[key] = new Date(record[key]).toISOString().split('T')[0];
                } catch (e) { initialData[key] = record[key]; }
            } else {
                if (Array.isArray(record[key])) {
                    initialData[key] = record[key].map(item => ({...item}));
                } else {
                    initialData[key] = record[key];
                }
            }
        });
        setEditData(initialData);
        setInitialEditData(deepCopy(initialData));
        setCurrentRecord(record);
        setIsAssigning(false);
    }, [record]);

    const handleEditChange = (e) => setEditData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSave = async () => {
        setIsSaving(true);
        setStatusMessage({ type: '', text: '' });
        
        const cleanedEditData = { ...editData };
        ['claimNo', 'adjNumber', 'doi'].forEach(field => {
            if (Array.isArray(cleanedEditData[field])) {
                cleanedEditData[field] = cleanedEditData[field].filter(item => item.value && item.value.trim() !== '');
            }
        });

        try {
            setEditData(cleanedEditData); 
            const updatedRecord = await onSave(currentRecord._id, cleanedEditData);
            
            setCurrentRecord(updatedRecord); 
            
            const initialData = {};
            Object.keys(updatedRecord).forEach(key => {
                if (['hearingDate', 'caseDate', 'dob', 'fds', 'lds', 'solDate'].includes(key) && updatedRecord[key]) {
                    try {
                        initialData[key] = new Date(updatedRecord[key]).toISOString().split('T')[0];
                    } catch (e) { initialData[key] = updatedRecord[key]; }
                } else {
                    if (Array.isArray(updatedRecord[key])) {
                        initialData[key] = updatedRecord[key].map(item => ({...item}));
                    } else {
                        initialData[key] = updatedRecord[key];
                    }
                }
            });
            setEditData(initialData); 
            setInitialEditData(deepCopy(initialData));

            setIsEditing(false);
            setStatusMessage({ type: 'success', text: 'Record saved successfully!' });
        } catch (error) {
            setStatusMessage({ type: 'error', text: 'Failed to save record.' });
        } finally {
            setIsSaving(false);
            setTimeout(() => setStatusMessage({ type: '', text: '' }), 4000);
        }
    };
    
    // NEW: Handler for the "Back" button click
    const handleBackClick = () => {
        if (isEditing && hasUnsavedChanges()) {
            setModalDiscardAction('back'); // Set action for "No"
            setShowUnsavedModal(true);
        } else {
            onBack();
        }
    };
    
    // NEW: Handler for the "Cancel" (edit) button click
    const handleCancelEditClick = () => {
        if (isEditing && hasUnsavedChanges()) {
            setModalDiscardAction('cancel'); // Set action for "No"
            setShowUnsavedModal(true);
        } else {
            // Just cancel editing if no changes
            setIsEditing(false);
            resetEditData();
        }
    };

    const handleAddComment = async (recordId, commentData) => {
        if (commentData) {
            const response = await api.post(`/records/${recordId}/comments`, commentData);
            setCurrentRecord(response.data);
            return response.data;
        } else {
            const response = await api.get(`/records/${recordId}`);
            setCurrentRecord(response.data);
        }
    };
    
    const handleStartAssign = () => {
        setIsAssigning(true);
        setSelectedCollectorForAssign(currentRecord.assignedCollector?._id || '');
    };
    
    const handleConfirmAssign = () => {
        if (selectedCollectorForAssign) {
            onAssign(currentRecord._id, selectedCollectorForAssign);
            setIsAssigning(false);
        } else {
            setStatusMessage({ type: 'error', text: 'Please select a collector.' });
        }
    };

    // --- Handlers for Multi-Entry fields ---
    const handleMultiEntryChange = (fieldName, index, value) => {
        setEditData(prev => {
            const newItems = prev[fieldName] ? [...prev[fieldName]] : [];
            if (newItems[index]) {
                newItems[index] = { ...newItems[index], value: value };
            } else {
                newItems[index] = { value: value };
            }
            return { ...prev, [fieldName]: newItems };
        });
    };

    const addMultiEntry = (fieldName) => {
        setEditData(prev => {
            const newItems = prev[fieldName] ? [...prev[fieldName]] : [];
            newItems.push({ value: '' });
            return { ...prev, [fieldName]: newItems };
        });
    };

    const removeMultiEntry = (fieldName, index) => {
        setEditData(prev => {
            const newItems = [...(prev[fieldName] || [])];
            newItems.splice(index, 1);
            return { ...prev, [fieldName]: newItems };
        });
    };
    // --- End Multi-Entry Handlers ---

    // --- FIELD DEFINITIONS ---
    const patientInfoFields = ['provider','renderingFacility','taxId','ptName', 'dob', 'ssn', 'employer', 'solDate', 'adjNumber', 'doi'];
    const billingInfoFields = ['bill', 'paid', 'outstanding', 'ledger', 'hcf', 'invoice', 'signinSheet', 'fds', 'lds'];
    const insuranceDefenseFields = ['insurance', 'claimNo', 'adjuster', 'adjusterPhone', 'adjusterFax', 'adjusterEmail', 'defenseAttorney', 'defenseAttorneyPhone', 'defenseAttorneyFax', 'defenseAttorneyEmail'];
    // MODIFIED: Added new fields
    const caseHearingFields = ['hearingStatus', 'hearingDate', 'hearingTime', 'judgeName','courtRoomlink','judgePhone','AccesCode','boardLocation', 'lienStatus', 'caseStatus', 'caseDate', 'crAmount', 'dorFiledBy', 'status4903_8', 'pmrStatus', 'judgeOrderStatus'];
    // --- END FIELD DEFINITIONS ---


    // Renders a non-editable field
    const renderField = (field) => {
        const amountFields = ['bill', 'paid', 'outstanding', 'crAmount'];
        const multiFields = ['adjNumber', 'doi', 'claimNo'];
        const data = isEditing ? editData : currentRecord;

        if (multiFields.includes(field)) {
            const items = data[field] || [];
            return (
                <div key={field} className="space-y-1 col-span-1 sm:col-span-2 md:col-span-3 lg:col-span-4">
                    <label className="block text-xs font-medium text-gray-500 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                    <div className="p-2 bg-gray-50 rounded border min-h-[40px]">
                        {items && items.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3">
                                {items.map((item, index) => (
                                    <div key={index} className="p-2 bg-gray-100 rounded text-center font-mono text-xs break-all">{item.value}</div>
                                ))}
                            </div>
                        ) : <p className="text-gray-500 text-sm">N/A</p>}
                    </div>
                </div>
            );
        }

        let displayValue = data[field];
        
        if (amountFields.includes(field)) {
            const value = parseFloat(displayValue);
            displayValue = !isNaN(value) 
                ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
                : 'N/A';
        } 
        else if (['dob', 'fds', 'lds', 'solDate', 'hearingDate', 'caseDate'].includes(field) && data[field]) {
            try {
                if (isEditing && typeof data[field] === 'string' && data[field].match(/^\d{4}-\d{2}-\d{2}$/)) {
                     // This branch is for edit mode, but renderField is only called in view mode.
                     // This logic is likely unused, but we'll correct it just in case.
                     const date = new Date(data[field] + 'T00:00:00'); // Interprets string as local
                     displayValue = date.toLocaleDateString();
                } else {
                    // --- THIS IS THE FIX ---
                    // The bug was here: new Date(data[field]).toLocaleDateString()
                    // This converts the UTC date from the DB to the browser's local timezone,
                    // which can shift the date by one day.
                    // By specifying timeZone: 'UTC', we display the date as it is in UTC.
                    displayValue = new Date(data[field]).toLocaleDateString(undefined, {
                        timeZone: 'UTC',
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric'
                    });
                    // --- END OF FIX ---
                }
            } catch (e) {
                displayValue = data[field];
            }
        }
        
        return (
            <div key={field} className="space-y-1">
                <label className="block text-xs font-medium text-gray-500 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                <p className="text-sm p-2 bg-gray-50 rounded border min-h-[40px] break-words">{displayValue || 'N/A'}</p>
            </div>
        );
    };

    // Renders an editable input field
    const renderEditableField = (field) => {
        const amountFields = ['bill', 'paid', 'outstanding', 'crAmount'];
        const dateFields = ['hearingDate', 'caseDate', 'dob', 'fds', 'lds', 'solDate'];
        const multiFields = ['adjNumber', 'doi', 'claimNo'];
        const dropdownFields = ['ledger', 'hcf', 'invoice', 'signinSheet' ];
        // MODIFIED: Added caseStatus field for dropdown
        const caseStatusField = 'caseStatus';
        const lienStatusField = 'lienStatus';
        // NEW: Options for the requested dropdowns
        const customDropdownOptions = {
            'dorFiledBy': [
                { value: '', label: 'Select Filer' },
                { value: 'HUBUR', label: 'HUBUR' },
                { value: 'CLIENT', label: 'CLIENT' },
                { value: 'ANOTHER LIEN CLAIMANT', label: 'ANOTHER LIEN CLAIMANT' }
            ],
            'status4903_8': [
                { value: '', label: 'Select Status' },
                { value: 'yes', label: 'yes' },
                { value: 'no', label: 'no' }
            ],
            'pmrStatus': [
                { value: '', label: 'Select Status' },
                { value: 'yes', label: 'yes' },
                { value: 'no', label: 'no' }
            ],
            'judgeOrderStatus': [
                { value: '', label: 'Select Status ' },
                { value: 'GRANTED', label: 'GRANTED' },
                { value: 'PENDING', label: 'PENDING' }
            ],
            'hearingTime': [
                { value: '', label: 'Select Time' },
                { value: 'AM', label: 'AM' },
                { value: 'PM', label: 'PM' }
            ]
        };

        if (multiFields.includes(field)) {
            const items = editData[field] || [];
            return (
                <div key={field} className="space-y-2 col-span-1 sm:col-span-2 md:col-span-3 lg:col-span-4">
                    <label className="block text-xs font-medium text-gray-700 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                    {items.map((item, index) => (
                        <div key={index} className="flex items-center gap-2">
                            <input 
                                type="text"
                                name={`${field}-${index}`}
                                value={item.value || ''}
                                onChange={(e) => handleMultiEntryChange(field, index, e.target.value)}
                                className="block w-full p-2 text-sm bg-white border border-gray-300 rounded"
                            />
                            <button type="button" onClick={() => removeMultiEntry(field, index)} className="text-red-500 hover:text-red-700 flex-shrink-0">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={() => addMultiEntry(field)} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1">
                        <PlusCircle className="w-4 h-4" /> Add {field.replace(/([A-Z])/g, ' $1')}
                    </button>
                </div>
            );
        }

        // NEW: Handle the new custom dropdowns
        if (customDropdownOptions[field]) {
            return (
                <div key={field} className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                    <select
                        name={field}
                        value={editData[field] || ''}
                        onChange={handleEditChange}
                        className="block w-full p-2 text-sm bg-white border border-gray-300 rounded"
                    >
                        {customDropdownOptions[field].map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            );
        }

        if (dropdownFields.includes(field)) {
            return (
              <div key={field} className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                    <select
                        name={field}
                        value={editData[field] || 'not required'}
                        onChange={handleEditChange}
                        className="block w-full p-2 text-sm bg-white border border-gray-300 rounded"
                    >
                        <option value="not required">Not Required</option>
                        <option value="yes">yes</option>
                        <option value="no">no</option>
                    </select>
                </div>
            );
        }

        // NEW: Handle caseStatus dropdown
        if (field === caseStatusField) {
            return (
                <div key={field} className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                    <select
                        name={field}
                        value={editData[field] || ''}
                        onChange={handleEditChange}
                        className="block w-full p-2 text-sm bg-white border border-gray-300 rounded"
                    >
                        {caseStatusOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            );
        }
 // NEW: Handle lienStatus dropdown
        if (field === lienStatusField) {
            return (
                <div key={field} className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                    <select
                        name={field}
                        value={editData[field] || ''}
                        onChange={handleEditChange}
                        className="block w-full p-2 text-sm bg-white border border-gray-300 rounded"
                    >
                        {lienStatusOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            );
        }
        const isAmount = amountFields.includes(field);
        const isDate = dateFields.includes(field);
        const inputType = isDate ? 'date' : (isAmount ? 'number' : 'text');
        
        return (
          <div key={field} className="space-y-1">
                <label className="block text-xs font-medium text-gray-700 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                <input 
                    type={inputType} 
                    name={field} 
                    value={editData[field] || ''} 
                    onChange={handleEditChange} 
                    className="block w-full p-2 text-sm bg-white border border-gray-300 rounded" 
                    {...(isAmount ? { step: '0.01' } : {})}
                />
            </div>
        );
    };

    if (!currentRecord) return null;

    return (
        <div className="h-full flex flex-col bg-gray-100">
            {/* --- REMOVED HEADER --- */}
            
            <header className="flex-shrink-0 bg-white border-b shadow-sm sticky top-0 z-10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
            <div className="flex-1 min-w-0">
                <h2 className="text-2xl font-bold text-gray-800 truncate">
                    Record: {currentRecord.ptName}
                </h2>
                <div className="mt-1 flex flex-wrap gap-x-4 text-sm text-gray-600">
                    {/* --- NEW: Reference ID Display with "Click to Fix" --- */}
                    <span className="bg-gray-100 px-2 py-0.5 rounded border border-gray-200 flex items-center gap-1">
                        <b>Ref ID:</b> {currentRecord.referenceId || 'N/A'}
                    </span>
                    {/* ------------------------------------------------ */}
                    <span>
                        <b>Status:</b> {currentRecord.caseStatus || 'N/A'}
                    </span>
                    <span>
                        <b>Collector:</b>{' '}
                        {currentRecord.assignedCollector?.username || 'Unassigned'}
                    </span>
                </div>
            </div>

            <div className="flex items-center gap-3 ml-4">
                {/* 🔐 Provider: no Edit, no Save, no Cancel */}
                {!isEditing ? (
                    !isProvider && (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg text-sm flex items-center gap-1"
                        >
                            <Edit className="w-4 h-4" /> Edit
                        </button>
                    )
                ) : (
                    !isProvider && (
                        <>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg disabled:bg-gray-400 text-sm"
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                onClick={handleCancelEditClick}
                                className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-lg text-sm"
                            >
                                Cancel
                            </button>
                        </>
                    )
                )}

                {/* Back is always allowed */}
                <button
                    onClick={handleBackClick}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-lg text-sm flex items-center gap-1"
                >
                    <ArrowLeft className="w-4 h-4" /> Back
                </button>
            </div>
        </div>
    </div>

    {statusMessage.text && (
        <div
            className={`px-4 py-2 text-sm ${
                statusMessage.type === 'success'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
            }`}
        >
            {statusMessage.text}
        </div>
    )}
</header>


            <main className="flex-1 overflow-y-auto">
                <div className="max-w-7xl mx-auto p-4 sm:px-6 lg:px-8 space-y-6">
                    
                    <Section title="Patient Information">
                        {isEditing 
                            ? patientInfoFields.map(field => renderEditableField(field)) 
                            : patientInfoFields.map(field => renderField(field))
                        }
                    </Section>

                    <Section title="Billing Information">
                        {isEditing ? billingInfoFields.map(field => renderEditableField(field)) : billingInfoFields.map(renderField)}
                    </Section>
                    
                    <Section title="Insurance & Defense Information">
                        {isEditing ? insuranceDefenseFields.map(field => renderEditableField(field)) : insuranceDefenseFields.map(renderField)}
                    </Section>
                    
                    <Section title="Case & Hearing (Eams Status)">
                        {isEditing ? caseHearingFields.map(field => renderEditableField(field)) : caseHearingFields.map(renderField)}
                    </Section>
                    
                    {onAssign && (
                        <Section title="Collector Assignment">
                            <div className="col-span-full">
                                {!isAssigning ? (
                                    <div className="space-y-2">
                                        <p className="font-medium">{currentRecord.assignedCollector?.username || 'Unassigned'}</p>
                                        <button onClick={handleStartAssign} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium underline">Re-assign</button>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <select value={selectedCollectorForAssign} onChange={e => setSelectedCollectorForAssign(e.target.value)} className="block max-w-xs p-2 text-sm border-gray-300 rounded-md">
                                            <option value="">Select a Collector</option>
                                            {collectors.map(c => <option key={c._id} value={c._id}>{c.username}</option>)}
                                        </select>
                                        <div className="flex gap-2">
                                            <button onClick={handleConfirmAssign} className="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-3 rounded-lg text-sm">Confirm</button>

                                            <button onClick={() => setIsAssigning(false)} className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-3 rounded-lg text-sm">Cancel</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Section>
                    )}
                    
                    <CommentSection comments={[...currentRecord.comments].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))} recordId={currentRecord._id} onCommentAdded={handleAddComment} user={user} api={api} />
                </div>
            </main>

            {/* NEW: Render the modal when showUnsavedModal is true */}
            {showUnsavedModal && (
                <UnsavedChangesModal
                    onYes={async () => {
                        // "Yes" always saves, stays on the page, and exits edit mode
                        await handleSave(); 
                        setShowUnsavedModal(false);
                        setModalDiscardAction(null);
                    }}
                    onNo={() => {
                        // "No" discards and performs the tracked action
                        setShowUnsavedModal(false);
                        resetEditData(); 
                        setIsEditing(false);

                        if (modalDiscardAction === 'back') {
                            onBack(); // Go back
                        }
                        // if 'cancel', it just stops editing (which it now has)
                        
                        setModalDiscardAction(null);
                    }}
                />
            )}
        </div>
    );
};

export default RecordDetails;